import { useState, useEffect, useRef, useCallback } from 'react'

/**
 * 防抖值 Hook
 * 返回一个延迟更新的值，用于减少频繁的 API 调用
 * 
 * @param value 原始值
 * @param delay 延迟时间（毫秒），默认 300ms
 * @returns 防抖后的值
 * 
 * @example
 * ```tsx
 * const [search, setSearch] = useState('')
 * const debouncedSearch = useDebouncedValue(search, 300)
 * 
 * // 使用 debouncedSearch 进行 API 调用
 * const { data } = useQuery({
 *   queryKey: ['search', debouncedSearch],
 *   queryFn: () => searchApi(debouncedSearch),
 *   enabled: debouncedSearch.length >= 2,
 * })
 * ```
 */
export function useDebouncedValue<T>(value: T, delay = 300): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value)

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)

    return () => {
      clearTimeout(timer)
    }
  }, [value, delay])

  return debouncedValue
}

/**
 * 防抖回调 Hook
 * 返回一个防抖的回调函数
 * 
 * @param callback 原始回调函数
 * @param delay 延迟时间（毫秒），默认 300ms
 * @returns 防抖后的回调函数
 * 
 * @example
 * ```tsx
 * const handleSearch = useDebouncedCallback((query: string) => {
 *   console.log('Searching:', query)
 * }, 300)
 * 
 * <input onChange={(e) => handleSearch(e.target.value)} />
 * ```
 */
export function useDebouncedCallback<T extends (...args: unknown[]) => unknown>(
  callback: T,
  delay = 300
): (...args: Parameters<T>) => void {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const callbackRef = useRef(callback)

  // 更新回调引用
  useEffect(() => {
    callbackRef.current = callback
  }, [callback])

  // 清理定时器
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  return useCallback(
    (...args: Parameters<T>) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }

      timeoutRef.current = setTimeout(() => {
        callbackRef.current(...args)
      }, delay)
    },
    [delay]
  )
}

/**
 * 防抖状态 Hook
 * 同时返回即时值和防抖值，适用于需要即时显示输入但延迟触发搜索的场景
 * 
 * @param initialValue 初始值
 * @param delay 延迟时间（毫秒），默认 300ms
 * @returns [即时值, 防抖值, 设置函数, 是否正在等待防抖]
 * 
 * @example
 * ```tsx
 * const [search, debouncedSearch, setSearch, isPending] = useDebouncedState('', 300)
 * 
 * <input
 *   value={search}
 *   onChange={(e) => setSearch(e.target.value)}
 *   placeholder={isPending ? '搜索中...' : '搜索'}
 * />
 * ```
 */
export function useDebouncedState<T>(
  initialValue: T,
  delay = 300
): [T, T, (value: T) => void, boolean] {
  const [value, setValue] = useState<T>(initialValue)
  const [debouncedValue, setDebouncedValue] = useState<T>(initialValue)
  const [isPending, setIsPending] = useState(false)

  useEffect(() => {
    if (value !== debouncedValue) {
      setIsPending(true)
    }

    const timer = setTimeout(() => {
      setDebouncedValue(value)
      setIsPending(false)
    }, delay)

    return () => {
      clearTimeout(timer)
    }
  }, [value, delay, debouncedValue])

  return [value, debouncedValue, setValue, isPending]
}

/**
 * 搜索防抖 Hook
 * 专门用于搜索场景，包含最小字符数限制
 * 
 * @param options 配置选项
 * @returns 搜索状态和方法
 * 
 * @example
 * ```tsx
 * const { query, debouncedQuery, setQuery, isSearching, clear } = useSearchDebounce({
 *   delay: 300,
 *   minLength: 2,
 * })
 * 
 * const { data } = useQuery({
 *   queryKey: ['search', debouncedQuery],
 *   queryFn: () => searchApi(debouncedQuery),
 *   enabled: debouncedQuery.length >= 2,
 * })
 * ```
 */
export function useSearchDebounce(options: {
  delay?: number
  minLength?: number
  initialValue?: string
} = {}) {
  const { delay = 300, minLength = 2, initialValue = '' } = options
  
  const [query, debouncedQuery, setQuery, isPending] = useDebouncedState(initialValue, delay)

  const clear = useCallback(() => {
    setQuery('')
  }, [setQuery])

  const isSearching = isPending && query.length >= minLength
  const shouldSearch = debouncedQuery.length >= minLength

  return {
    /** 即时查询值 */
    query,
    /** 防抖后的查询值 */
    debouncedQuery,
    /** 设置查询值 */
    setQuery,
    /** 是否正在等待防抖 */
    isPending,
    /** 是否正在搜索（等待防抖且满足最小长度） */
    isSearching,
    /** 是否应该执行搜索（满足最小长度） */
    shouldSearch,
    /** 清空搜索 */
    clear,
  }
}
